import React, { useEffect, useState, useMemo } from 'react';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailModal from './TicketDetailModal';
import Toast from './Toast';
import KanbanBoard from './KanbanBoard';

// TicketsPage.jsx - página principal de tickets (integra CreateTicketModal)
export default function TicketsPage({ apiBase = '/api/tickets_advanced_jwt.php' }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  // filtros
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [service, setService] = useState('');
  const [assignee, setAssignee] = useState('');

  const [selected, setSelected] = useState(new Set());
  const [selectAllOnPage, setSelectAllOnPage] = useState(false);

  const [servicesList, setServicesList] = useState([]);
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [token, setToken] = useState(localStorage.getItem('auth_token') || null);
  const [showLogin, setShowLogin] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showKanban, setShowKanban] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { fetchServices(); }, []);
  useEffect(() => { fetchTickets(); }, [page, pageSize, q, status, priority, service, assignee]);

  async function fetchServices() {
    try {
      const res = await fetch('/api/services.php');
      if (!res.ok) return;
      const data = await res.json();
      setServicesList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load services', e);
    }
  }

  function buildQuery() {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('page_size', pageSize);
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (service) params.set('service', service);
    if (assignee) params.set('assignee', assignee);
    return params.toString();
  }

  async function fetchTickets() {
    setLoading(true);
    try {
      const qs = buildQuery();
      const res = await fetch(`${apiBase}?${qs}`, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} });
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
      setSelected(new Set());
      setSelectAllOnPage(false);
    } catch (e) {
      console.error('Failed to fetch tickets', e);
      setToast({ message: 'Falha ao obter tickets', type: 'error' });
    } finally { setLoading(false); }
  }

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  function toggleSelect(id) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  }

  function toggleSelectAll() {
    if (selectAllOnPage) { setSelected(new Set()); setSelectAllOnPage(false); return; }
    const s = new Set(tickets.map(t => t.id)); setSelected(s); setSelectAllOnPage(true);
  }

  async function bulkAction(action) {
    if (selected.size === 0) return setToast({ message: 'Nenhum ticket selecionado', type: 'info' });
    const ids = Array.from(selected);
    const body = { action, ids };
    if (action === 'assign') {
      const email = prompt('Email do técnico/assignee:');
      if (!email) return;
      body.assignee_email = email;
    }
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}?bulk=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? ('Bearer ' + token) : '' },
        body: JSON.stringify(body)
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Bulk failed');
      setToast({ message: `Bulk action completa — ${j.changed || 0} alterados`, type: 'success' });
      fetchTickets();
    } catch (e) {
      console.error(e);
      setToast({ message: 'Falhou a ação em lote', type: 'error' });
    } finally { setBusy(false); }
  }

  function exportCsv() {
    const qs = buildQuery();
    window.open(`${apiBase}?${qs}&export=csv`, '_blank');
  }

  async function updateTicket(id, patch) {
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}?id=${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? ('Bearer ' + token) : '' },
        body: JSON.stringify(patch)
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Update failed');
      setToast({ message: 'Ticket atualizado', type: 'success' });
      fetchTickets();
    } catch (e) {
      console.error('Update failed', e);
      setToast({ message: 'Falha ao atualizar', type: 'error' });
    } finally { setBusy(false); }
  }

  // authentication (dev) - simple login to get token from /api/auth.php
  async function login(email) {
    try {
      const res = await fetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Login failed');
      setToken(j.token);
      localStorage.setItem('auth_token', j.token);
      setShowLogin(false);
      setToast({ message: 'Autenticado', type: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ message: 'Falha ao autenticar', type: 'error' });
    }
  }

  function logout() {
    setToken(null);
    localStorage.removeItem('auth_token');
    setToast({ message: 'Logout efetuado', type: 'info' });
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Painel de Tickets</h2>
        <div className="flex gap-2 items-center">
          <button onClick={() => setShowKanban(s => !s)} className="px-3 py-1 rounded border">{showKanban ? 'Lista' : 'Kanban'}</button>
          <button onClick={() => setShowCreate(true)} className="px-3 py-1 rounded border">Novo Ticket</button>
          <button onClick={exportCsv} className="px-3 py-1 rounded shadow-sm border">Export CSV</button>
          <button onClick={() => fetchTickets()} className="px-3 py-1 rounded bg-slate-800 text-white">Atualizar</button>
          {!token ? <button onClick={() => setShowLogin(true)} className="px-3 py-1 rounded border">Login</button> : <button onClick={logout} className="px-3 py-1 rounded border">Logout</button>}
        </div>
      </div>

      {showKanban ? (
        <KanbanBoard apiBase={apiBase} onTicketClick={id => setDetailId(id)} token={token} />
      ) : (
        <>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Pesquisar (q)" className="p-2 border rounded" />
            <select value={status} onChange={e => setStatus(e.target.value)} className="p-2 border rounded">
              <option value="">Estado (todos)</option>
              <option value="open">Aberto</option>
              <option value="in_progress">Em progresso</option>
              <option value="waiting">Aguardando</option>
              <option value="closed">Fechado</option>
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)} className="p-2 border rounded">
              <option value="">Prioridade (todas)</option>
              <option value="low">Baixa</option>
              <option value="normal">Normal</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
            <select value={service} onChange={e => setService(e.target.value)} className="p-2 border rounded">
              <option value="">Serviço (todos)</option>
              {servicesList.map(s => (
                <option key={s.id || s.key || s.name} value={s.id || s.key || s.name}>{s.name || s.id || s.key}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <button disabled={busy} onClick={() => bulkAction('close')} className="px-3 py-1 rounded border">Fechar selecionados</button>
            <button disabled={busy} onClick={() => bulkAction('assign')} className="px-3 py-1 rounded border">Atribuir selecionados</button>
            <div className="ml-auto">Página {page} de {pageCount}</div>
          </div>

          {/* Tickets table */}
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2"><input type="checkbox" checked={selectAllOnPage} onChange={toggleSelectAll} /></th>
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Assunto</th>
                  <th className="p-2 text-left">Solicitante</th>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Prioridade</th>
                  <th className="p-2 text-left">Atribuído</th>
                  <th className="p-2 text-left">Atualizado</th>
                  <th className="p-2 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="p-4 text-center">A carregar...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={9} className="p-4 text-center">Nenhum ticket</td></tr>
                ) : tickets.map(t => (
                  <tr key={t.id} className="border-t">
                    <td className="p-2"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} /></td>
                    <td className="p-2">{t.external_id || t.id}</td>
                    <td className="p-2 max-w-xl truncate cursor-pointer" onClick={() => setDetailId(t.id)}>{t.title}</td>
                    <td className="p-2">{t.requester_email}</td>
                    <td className="p-2">{t.status}</td>
                    <td className="p-2">{t.priority}</td>
                    <td className="p-2">{t.assignee_email || '-'}</td>
                    <td className="p-2">{t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}</td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <button disabled={busy} onClick={() => updateTicket(t.id, { status: 'in_progress' })} className="px-2 py-1 rounded border">Iniciar</button>
                        <button disabled={busy} onClick={() => updateTicket(t.id, { status: 'closed' })} className="px-2 py-1 rounded border">Fechar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 rounded border">Anterior</button>
              <button disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} className="px-2 py-1 rounded border">Próxima</button>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="p-2 border rounded">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">Total: {total} tickets</div>
          </div>
        </>
      )}

      {/* Login modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-96">
            <h3 className="font-semibold mb-2">Autenticar (dev)</h3>
            <p className="text-sm text-gray-600 mb-2">Insere o teu email para obter token (desenvolvimento)</p>
            <LoginForm onLogin={login} onClose={() => setShowLogin(false)} />
          </div>
        </div>
      )}

      {/* Create Ticket modal */}
      {showCreate && (
        <CreateTicketModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(t) => { setToast({ message: 'Ticket criado', type: 'success' }); fetchTickets(); }}
          apiBase={apiBase}
          token={token}
        />
      )}

      {/* Detail modal */}
      {detailId && <TicketDetailModal ticketId={detailId} onClose={() => setDetailId(null)} apiBase={apiBase} token={token} />}

      {/* Toast */}
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'info' })} />

      {/* small busy overlay */}
      {busy && <div className="fixed inset-0 z-40 flex items-center justify-center"><div className="p-3 bg-white rounded shadow">A processar...</div></div>}
    </div>
  );
}

// small LoginForm used in the page
function LoginForm({ onLogin, onClose }) {
  const [email, setEmail] = useState('');
  return (
    <>
      <input className="w-full p-2 border rounded mb-2" placeholder="email" value={email} onChange={e => setEmail(e.target.value)} />
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1 rounded border">Cancelar</button>
        <button onClick={() => onLogin(email)} className="px-3 py-1 rounded bg-slate-800 text-white">Login</button>
      </div>
    </>
  );
}
